import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Calendar, Clock, MapPin, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getArchivedEvents, unarchiveEvent, Event } from '../../services/events';
import toast from 'react-hot-toast';

export default function ArchivePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null);

  useEffect(() => {
    const loadArchived = async () => {
      if (!user?.uid) { setLoading(false); return; }
      try {
        const archived = await getArchivedEvents(user.uid);
        setEvents(archived);
      } catch {
        toast.error('Failed to load archived events');
      } finally {
        setLoading(false);
      }
    };
    loadArchived();
  }, [user?.uid]);

  const handleUnarchive = async (eventId: string) => {
    setUnarchivingId(eventId);
    try {
      await unarchiveEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event moved back to dashboard (still locked)');
    } catch {
      toast.error('Failed to unarchive event');
    } finally {
      setUnarchivingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Archive className="w-7 h-7 text-primary" />
          Archive
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Completed events removed from your dashboard.
        </p>
      </div>

      {events.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Archive className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Archived Events</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Completed events can be archived from the dashboard.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <div
              key={event.id}
              className="card px-4 py-4 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white truncate" title={event.name}>
                  {event.name}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(event.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}
                  </span>
                  {event.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {event.location}
                    </span>
                  )}
                  {event.archivedAt && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Archived {new Date(event.archivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/events/${event.id}/manage/queue`)}
                  className="btn btn-secondary btn-sm flex items-center gap-1.5"
                  title="View event (read-only)"
                >
                  <ExternalLink className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => handleUnarchive(event.id)}
                  disabled={unarchivingId === event.id}
                  className="btn btn-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 flex items-center gap-1.5"
                  title="Move back to dashboard (stays locked)"
                >
                  {unarchivingId === event.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                  Unarchive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
